import * as React from "react";
import { Field } from "formik";
import { TextField } from "formik-material-ui";
import { useCallback, useState, useMemo, memo } from "react";
import {
  makeStyles,
  Grid,
  MenuItem,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@material-ui/core";
import * as R from "ramda";

import DriverInputs from "../../driverDetails/components/driverInputs";
import Autocomplete from "../../../components/Autocomplete";
import { PrimaryButton } from "./primaryButton";
import { validateRouteNumber } from "../validators";
import {
  CREATE_ROUTE,
  MAIN_DRIVERS,
  NEED_CREATE_ROUTE_MANUALLY,
  NEXT,
  NO_OPTIONS,
  ODF,
  OTHER_DRIVERS,
  ROUTE_NUMBER,
  SECTION,
  DRIVER,
} from "../../../constants/strings";
import CheckboxIcon from "../../../components/icons/Checkbox";
import { isDriverInputDisabled } from "../model";
import { View } from "../../../components/Base";

const useStyles = makeStyles(({ typography }) => ({
  inputs: {
    minHeight: "13rem",
    margin: "0.75rem 0",
  },
  buttons: {
    padding: 0,
  },
  inBuildConfirm: {
    margin: 0,
    alignItems: "flex-start",
  },
  label: {
    paddingTop: typography.pxToRem(2),
    fontSize: typography.pxToRem(11),
    marginLeft: typography.pxToRem(8),
  },
}));

const AddRouteForm = ({
  drivers,
  driver,
  vehicles,
  isSubmitting,
  handleNext,
  handleSelectDriver,
  handleSelectSection,
  handleSelectOdfDriver,
  dirty,
  sections,
  routeNumber,
  values,
  isMultiRunRoute,
  routeNumberRef,
  isRouteInBuild,
  odfDriver,
  odfDrivers,
}) => {
  const styles = useStyles();

  const onNext = useCallback(() => {
    handleNext(values);
  }, [handleNext, values]);

  const [confirm, setConfirm] = useState(false);

  const onChangeConfirm = useCallback((e) => setConfirm(e.target.checked), []);

  const isCreateRouteEnable = isRouteInBuild
    ? !isSubmitting && confirm
    : !isSubmitting;

  const onSectionDropdownClick = useCallback(
    (e) => {
      e.target.value && handleSelectSection(e.target.value);
    },
    [handleSelectSection]
  );

  const onRouteNumberEnterClick = useCallback(
    (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        onNext();
      }
      if (!/[0-9]/.test(e.key) && e.key !== "Backspace") {
        e.preventDefault();
      }
    },
    [onNext]
  );

  const isSelectDriverDisabled = useMemo(
    () => isDriverInputDisabled(isSubmitting, isMultiRunRoute, isRouteInBuild),
    [isSubmitting, isMultiRunRoute, isRouteInBuild]
  );

  const groupByDrivers = useCallback(
    (option) => (option.isActive ? MAIN_DRIVERS : OTHER_DRIVERS),
    []
  );

  return (
    <>
      <View className={styles.inputs}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Field
              component={TextField}
              name="routeNumber"
              type="text"
              label={ROUTE_NUMBER}
              variant="outlined"
              fullWidth
              disabled={routeNumber}
              data-testid="route-number"
              inputRef={routeNumberRef}
              validate={validateRouteNumber}
              onKeyDown={onRouteNumberEnterClick}
            />
          </Grid>
          {routeNumber && (
            <Grid item xs={6}>
              <Field
                component={TextField}
                select
                name="sectionCode"
                variant="outlined"
                margin="normal"
                label={SECTION}
                fullWidth
                data-testid="section"
                onClick={onSectionDropdownClick}
              >
                {!R.isEmpty(sections) ? (
                  sections.map((section) => (
                    <MenuItem
                      value={section.sectionCode}
                      key={section.sectionCode}
                    >
                      {section.sectionName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">{NO_OPTIONS}</MenuItem>
                )}
              </Field>
            </Grid>
          )}
        </Grid>
        {routeNumber && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Field
                component={Autocomplete}
                value={odfDriver?.driverCode}
                onChange={handleSelectOdfDriver}
                options={odfDrivers}
                label={ODF}
                dataTestId="select-odfdriver"
                name="odfDriverCode"
                disabled={isSelectDriverDisabled}
                groupBy={groupByDrivers}
                popperProps={{ placement: "bottom-start" }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                component={Autocomplete}
                value={driver?.driverCode}
                onChange={handleSelectDriver}
                options={drivers}
                label={DRIVER}
                dataTestId="select-driver"
                name="driverCode"
                disabled={isSelectDriverDisabled}
                groupBy={groupByDrivers}
                popperProps={{ placement: "bottom-start" }}
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}
        {routeNumber && driver.driverCode && (
          <DriverInputs vehicles={vehicles} />
        )}
      </View>
      <DialogActions className={styles.buttons}>
        <Grid container spacing={2} justify="flex-end">
          {routeNumber && isRouteInBuild && (
            <Grid item xs={8} justify="flex-start">
              <FormControlLabel
                className={styles.inBuildConfirm}
                label={
                  <Typography className={styles.label}>
                    {NEED_CREATE_ROUTE_MANUALLY}
                  </Typography>
                }
                control={
                  <Checkbox
                    disableRipple
                    checked={confirm}
                    onChange={onChangeConfirm}
                    icon={<CheckboxIcon />}
                    checkedIcon={<CheckboxIcon checked />}
                  />
                }
              />
            </Grid>
          )}
          <Grid item xs={4} container justify="flex-end">
            {routeNumber ? (
              <PrimaryButton
                type="submit"
                key="Submit"
                disabled={!isCreateRouteEnable}
                title={CREATE_ROUTE}
              />
            ) : (
              <PrimaryButton
                key="Next"
                disabled={!dirty}
                title={NEXT}
                handleClick={onNext}
              />
            )}
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

export default memo(AddRouteForm);
